// Investment & registered-account growth calculators.
import { futureValue, paymentForFV, round2 } from "./finance-math";
import { estimateTax } from "../tax";
import { ACCOUNT_LIMITS } from "../financial-data/canada";

// Build a year-by-year growth series for charting.
function growthSeries({ initial, annualContribution, annualReturn, years, compound = 1, contribAtStart = false }) {
  const rate = annualReturn / 100 / compound;
  const contribPerPeriod = annualContribution / compound;
  const series = [{ year: 0, balance: round2(initial), contributions: round2(initial), growth: 0 }];
  let balance = initial;
  let totalContrib = initial;
  for (let y = 1; y <= years; y++) {
    for (let c = 0; c < compound; c++) {
      if (contribAtStart) balance += contribPerPeriod;
      balance = balance * (1 + rate);
      if (!contribAtStart) balance += contribPerPeriod;
      totalContrib += contribPerPeriod;
    }
    series.push({
      year: y,
      balance: round2(balance),
      contributions: round2(totalContrib),
      growth: round2(balance - totalContrib),
    });
  }
  return { series, finalBalance: balance, totalContributions: totalContrib };
}

export function compoundInterest({ principal, monthlyContribution = 0, annualRate, years, compoundsPerYear = 12 }) {
  const p = Math.max(0, Number(principal) || 0);
  const monthly = Math.max(0, Number(monthlyContribution) || 0);
  const rate = Math.max(0, Number(annualRate) || 0);
  const yrs = Math.max(0, Number(years) || 0);
  const comp = Number(compoundsPerYear) || 12;
  const annualContribution = monthly * 12;
  const { series, finalBalance, totalContributions } = growthSeries({
    initial: p, annualContribution, annualReturn: rate, years: yrs, compound: comp,
  });
  return {
    futureValue: round2(finalBalance),
    totalContributions: round2(totalContributions),
    totalInterest: round2(finalBalance - totalContributions),
    series,
  };
}

// Generic investment growth used by TFSA / RRSP / FHSA growth calculators.
export function investmentGrowth({ initial, annualContribution, annualReturn, years }) {
  const { series, finalBalance, totalContributions } = growthSeries({
    initial: Math.max(0, Number(initial) || 0),
    annualContribution: Math.max(0, Number(annualContribution) || 0),
    annualReturn: Math.max(0, Number(annualReturn) || 0),
    years: Math.max(0, Number(years) || 0),
    compound: 1,
  });
  return {
    futureValue: round2(finalBalance),
    totalContributions: round2(totalContributions),
    totalGrowth: round2(finalBalance - totalContributions),
    series,
  };
}

// TFSA growth — also estimates (very roughly) contribution room accumulated (documented as estimate).
export function tfsaGrowth({ initial, annualContribution, annualReturn, years }) {
  const g = investmentGrowth({ initial, annualContribution, annualReturn, years });
  const annualLimit = ACCOUNT_LIMITS.tfsa.annual;
  const contributed = Math.min(Number(annualContribution) || 0, annualLimit);
  return {
    ...g,
    annualLimit,
    withinAnnualLimit: (Number(annualContribution) || 0) <= annualLimit,
    contributedWithinLimit: round2(contributed),
  };
}

export function rrspGrowth({ initial, annualContribution, annualReturn, years }) {
  return investmentGrowth({ initial, annualContribution, annualReturn, years });
}

// RRSP tax savings — refund estimate from contribution at marginal rate.
export function rrspTaxSavings({ annualIncome, contribution, province = "ON", room = 0 }) {
  const income = Math.max(0, Number(annualIncome) || 0);
  const contrib = Math.max(0, Number(contribution) || 0);
  const before = estimateTax(income, province);
  const after = estimateTax(Math.max(0, income - contrib), province);
  const refund = before.totalTax - after.totalTax;
  const rrspRoom = round2(income * ACCOUNT_LIMITS.rrsp.rate);
  const estRoom = Math.min(rrspRoom, ACCOUNT_LIMITS.rrsp.maxDollar);
  const effectiveRoom = Number(room) > 0 ? Number(room) : estRoom;
  return {
    estimatedRefund: round2(refund),
    marginalRate: round2(before.marginalRate * 100),
    effectiveCost: round2(contrib - refund),
    taxBefore: round2(before.totalTax),
    taxAfter: round2(after.totalTax),
    maxRoomEstimate: estRoom,
    effectiveRoom: round2(effectiveRoom),
    roomFromCRA: Number(room) > 0,
    withinRoom: contrib <= effectiveRoom,
    excess: round2(Math.max(0, contrib - effectiveRoom)),
  };
}

// FHSA — growth plus tax deduction benefit (like RRSP) and lifetime cap awareness.
export function fhsaGrowth({ initial, annualContribution, annualReturn, years, annualIncome, province = "ON" }) {
  const g = investmentGrowth({ initial, annualContribution, annualReturn, years });
  const contrib = Math.max(0, Number(annualContribution) || 0);
  const before = estimateTax(Number(annualIncome) || 0, province);
  const after = estimateTax(Math.max(0, (Number(annualIncome) || 0) - contrib), province);
  const annualRefund = before.totalTax - after.totalTax;
  return {
    ...g,
    annualLimit: ACCOUNT_LIMITS.fhsa.annual,
    lifetimeLimit: ACCOUNT_LIMITS.fhsa.lifetime,
    withinAnnualLimit: contrib <= ACCOUNT_LIMITS.fhsa.annual,
    annualTaxRefund: round2(annualRefund),
  };
}

// RESP — contributions + CESG grant (20% up to $500/yr, $7,200 lifetime) + growth.
export function respGrowth({ initial, annualContribution, annualReturn, years, cesgRate, cesgAnnualMax, cesgLifetimeMax }) {
  const p = Math.max(0, Number(initial) || 0);
  const annual = Math.max(0, Number(annualContribution) || 0);
  const rate = Math.max(0, Number(annualReturn) || 0) / 100;
  const yrs = Math.max(0, Number(years) || 0);
  const gRate = cesgRate ?? ACCOUNT_LIMITS.resp.cesgRate;
  const gAnnualMax = cesgAnnualMax ?? ACCOUNT_LIMITS.resp.cesgAnnualMax;
  const gLifetimeMax = cesgLifetimeMax ?? ACCOUNT_LIMITS.resp.cesgLifetimeMax;

  let balance = p;
  let totalContrib = p;
  let totalGrant = 0;
  const series = [{ year: 0, balance: round2(balance), contributions: round2(totalContrib), grants: 0 }];
  for (let y = 1; y <= yrs; y++) {
    const grant = Math.min(annual * gRate, gAnnualMax, Math.max(0, gLifetimeMax - totalGrant));
    balance += annual + grant;
    balance *= 1 + rate;
    totalContrib += annual;
    totalGrant += grant;
    series.push({
      year: y,
      balance: round2(balance),
      contributions: round2(totalContrib),
      grants: round2(totalGrant),
    });
  }
  return {
    futureValue: round2(balance),
    totalContributions: round2(totalContrib),
    totalGrants: round2(totalGrant),
    totalGrowth: round2(balance - totalContrib - totalGrant),
    series,
  };
}

// GIC — simple or compound interest.
export function gicReturn({ principal, annualRate, years, compoundsPerYear = 1, simple = false }) {
  const p = Math.max(0, Number(principal) || 0);
  const rate = Math.max(0, Number(annualRate) || 0) / 100;
  const yrs = Math.max(0, Number(years) || 0);
  const comp = Number(compoundsPerYear) || 1;
  let maturity;
  const series = [{ year: 0, balance: round2(p) }];
  if (simple) {
    for (let y = 1; y <= yrs; y++) series.push({ year: y, balance: round2(p * (1 + rate * y)) });
    maturity = p * (1 + rate * yrs);
  } else {
    for (let y = 1; y <= yrs; y++) series.push({ year: y, balance: round2(p * Math.pow(1 + rate / comp, comp * y)) });
    maturity = p * Math.pow(1 + rate / comp, comp * yrs);
  }
  return {
    maturityValue: round2(maturity),
    totalInterest: round2(maturity - p),
    principal: round2(p),
    series,
  };
}

export { futureValue, paymentForFV };
